import { z } from 'zod';
/**
 * **The tenant's own export** — spec §7.5's "the owner can always leave with
 * their book", as a document with a shape.
 *
 * §7.5 promises it and §7.6 leans on it: on offboarding *the tenant receives
 * their full export and the data is deleted 90 days later*. Until 2026-09-22
 * that was a sentence (`docs/plan-2026-09-21.md`, sixth pass: "there is no
 * per-tenant export function"). This file is the part every surface must agree
 * on letter for letter — which tables, in what order, with what left out, and
 * how a reader proves the file it holds is the file that was written. The SQL
 * that reads the rows is `packages/store`'s (`repos/export.ts`); the reader
 * that lands one back is `packages/importer`'s (`from-export.ts`); the verb is
 * `yourstore tenant export`.
 *
 * ## What the document is
 *
 * One JSON object per table the tenant owns, **every row as stored**: localized
 * maps as maps, cents as integers, instants as ISO-8601, half-open ranges as
 * `{ "start", "end" }`. Not a projection and not a report — a report is
 * `apps/reporting-api`'s and can be re-run; this is the record, and the reason
 * it is the raw rows is that a projection chosen today is a projection somebody
 * will need a column from in three years. The one thing that is *not* as
 * stored is a secret (below).
 *
 * ## The order
 *
 * `TENANT_EXPORT_TABLES` is **parents before children**. It is the seed's
 * delete order (`deleteTenantData` in `packages/store/src/seed/fixtures.ts`)
 * reversed, and reversing a delete order gives an insert order for the same
 * reason the delete order works at all: a delete must remove a child before the
 * parent a RESTRICT key points at, so reading it backwards puts every parent
 * before every child that points at it — which is exactly what an INSERT needs.
 * A reader that lands the document table by table in this order never meets a
 * foreign key whose target is not there yet. Two footnotes, both from checking
 * the FK graph rather than trusting the sentence:
 *
 *  - the seed's order lists only the tables it *must* — the ones a RESTRICT key
 *    points from — and lets the rest cascade from `tenants`. Twenty-eight
 *    tables (refunds among them, which is money) are therefore not in the
 *    seed's list at all, and are slotted here after their parents;
 *  - one pair in the seed's order is only valid for deleting: `credit_entries`
 *    is deleted *before* `settlements`, which works because
 *    `credit_entries.settlement_id` is `ON DELETE SET NULL` — but reversed,
 *    it would insert a credit entry pointing at a settlement not yet written.
 *    Here it follows `settlements`, as `writeImportRows` already knew.
 *
 * `packages/store`'s `export.test.ts` proves both: the roster names every
 * table with a `tenant_id` and nothing else, and every foreign key's target
 * precedes its source. A table added tomorrow fails that test until it is
 * placed here, which is the point — a tenant export that quietly omits a
 * table is the failure mode this list exists to make loud.
 *
 * ## What is left out, by name
 *
 * **A secret is never in a document.** The rows of `api_keys` are hers to know
 * exist — which integration, which scopes, when last used — and not to have:
 * `key_hash` is omitted. The same for every other credential the platform
 * holds *for* her rather than *about* her: the OTP hash, the album code hash,
 * the intake code hash, the iCal feed token hash, and the plaintext one-time
 * code an unsent `otp.requested` event still carries in its payload. A
 * `secret_ref` is a Secret Manager *pointer* (spec §7.1) and exports as the
 * reference it is. Her own OTA calendar URLs (`item_lodging.external_calendars`,
 * `external_calendar_pulls.ref`) are hers and stay: the rule is about what we
 * hold for her, not what she gave us.
 *
 * ## What lands back, and what does not
 *
 * `lands` marks the tables the round trip re-creates in a fresh tenant: the
 * book, the clients, the ledger, the catalog, the configuration. The rest are
 * exported for completeness and never landed, for one of three reasons stated
 * on each row: it is a platform credential (a key without its hash is not a
 * key), it is keyed by something globally unique that a second tenant cannot
 * hold (a hostname, a Firebase uid, a PayPal order id), or it is operational
 * state — the outbox, the delivery queues, holds, counters — that describes
 * this platform's work rather than her business.
 *
 * ## The manifest
 *
 * The document ends with a `manifest`: the tenant, the instant, the row count
 * per table, the schema's migration, and the SHA-256 of the **body** — the
 * exact bytes of the `tables` value as written. The hash is over bytes rather
 * than over a re-serialization so that a reader verifies the file it holds,
 * not a file it could have produced. `splitTenantExport` is the one place the
 * layout is known; `parseTenantExport` takes the hash function as an argument
 * so that this package keeps depending on nothing but zod and ulid.
 */
/* ------------------------------------------------------------------ */
/* Version                                                             */
/* ------------------------------------------------------------------ */
export const TENANT_EXPORT_FORMAT = 'yourstore.tenant-export';
/**
 * Bumped when the layout or the roster's *meaning* changes — not when a table
 * is added. A reader of version 1 handles any roster, because the roster is
 * data in the document (the manifest's `row_counts` keys) and the reader lands
 * what it recognizes.
 */
export const TENANT_EXPORT_VERSION = 1;
const business = (table) => ({ table, lands: true, omit: [] });
const platform = (table, why, omit = []) => ({
    table,
    lands: false,
    omit,
    why,
});
/**
 * Every table a tenant owns, parents first. See the file comment for why this
 * order is the seed's delete order reversed, and for the two corrections.
 */
export const TENANT_EXPORT_TABLES = [
    // The tenant row itself: name, timezone, currency, status, plan. Landed by
    // `createTenant` rather than by row copy, because a landed tenant has a new
    // id and a new slug, and because its status is never copied — `live` is
    // earned through the reconciliation gate (spec §4.4), not landed.
    { table: 'tenants', lands: true, omit: [] },
    business('tenant_config'),
    business('tenant_modules'),
    platform('audit_log', 'the operator trail is about her tenant, not of it; read, never replayed'),
    platform('staff_users', 'a mirror of Identity Platform; `firebase_uid` is unique across tenants'),
    platform('api_keys', 'a key without its hash is a record of a key, not a key', ['key_hash']),
    business('resources'),
    platform('staff_resources', 'points at `staff_users`, which does not land'),
    business('blackouts'),
    business('resource_hours'),
    business('items'),
    business('item_options'),
    business('item_add_ons'),
    business('item_media'),
    business('item_menu'),
    business('modifier_groups'),
    business('modifiers'),
    business('item_departure'),
    business('item_retail'),
    business('item_scheduling'),
    business('item_resources'),
    // After `items` and `resources` (0021), which the seed leaves to the cascade.
    business('item_lodging'),
    business('item_fulfillment'),
    business('item_fulfillment_variants'),
    // The OTA seams (0060): her calendars and what the last pull did. After
    // `item_lodging`, which `external_calendar_pulls` points at.
    business('external_calendar_pulls'),
    business('external_busy'),
    platform('ical_feed_tokens', 'the feed token is a credential; its label and unit stay', [
        'token_hash',
    ]),
    business('clients'),
    business('client_identities'),
    business('client_service_durations'),
    business('client_preferences'),
    business('client_products'),
    business('sms_consent_asks'),
    business('certificates'),
    business('data_requests'),
    // After `certificates`: `fulfilled_certificate_id` (0051).
    business('gift_requests'),
    business('settlements'),
    business('settlement_lines'),
    // After `settlements` — the one place the seed's order is delete-only (see
    // the file comment): `credit_entries.settlement_id`.
    business('credit_entries'),
    // Refunds (0046): after the settlement or certificate each one names.
    business('refunds'),
    business('refund_lines'),
    platform('payment_links', 'the PayPal order id is unique across tenants; the money it moved is on the deposit it settled'),
    business('orders'),
    business('order_lines'),
    business('order_line_modifiers'),
    business('order_fulfillment'),
    business('reservations'),
    business('reservation_lines'),
    business('departure_instances'),
    platform('holds', 'a ten-minute hold is a moment, not a record'),
    business('appointments'),
    business('appointment_resources'),
    business('appointment_services'),
    platform('photo_albums', 'the album code is a credential; the album is objects in a bucket', [
        'code_hash',
    ]),
    platform('photo_album_media', 'points at `photo_albums`, which does not land'),
    business('newsletter_subscriptions'),
    business('newsletter_issues'),
    platform('newsletter_deliveries', 'a send log: operational, and keyed by the address it went to'),
    business('email_suppressions'),
    platform('tenant_domains', 'a hostname is unique across tenants; it carries no credential'),
    platform('tenant_intakes', 'the intake code is a credential; the answers are onboarding, not the book', ['code_hash']),
    platform('intake_uploads', 'points at `tenant_intakes`, which does not land'),
    platform('guest_otp_codes', 'a one-time code is a credential, spent or not', ['code_hash']),
    platform('usage_counters', 'admission counters: this platform’s work, not her business'),
    platform('webhook_endpoints', 'the signing secret is a Secret Manager reference and exports as one'),
    platform('idempotency_keys', 'the partner’s replay cache; thirty days of it'),
    platform('events', 'the outbox; an unsent `otp.requested` still carries its plaintext code (migration 0013)', ['payload.code']),
    platform('webhook_deliveries', 'delivery attempts to the partner'),
    platform('sms_deliveries', 'delivery attempts to a guest'),
    platform('appointment_reminders', 'the reminder schedule; a row per send, keyed by its offset'),
    platform('owner_alert_queue', 'what the owner has not been told yet'),
    platform('owner_alert_cursor', 'where the alert loop stopped'),
];
/** The tables the round trip re-creates, in landing order. */
export const TENANT_EXPORT_LANDED_TABLES = TENANT_EXPORT_TABLES.filter((t) => t.lands).map((t) => t.table);
export function tenantExportTable(table) {
    return TENANT_EXPORT_TABLES.find((t) => t.table === table);
}
/* ------------------------------------------------------------------ */
/* The manifest and the layout                                         */
/* ------------------------------------------------------------------ */
export const TenantExportManifest = z.object({
    tenant_id: z.string().min(1),
    exported_at: z.string().datetime(),
    /**
     * The schema the rows were read under: the applied migration's journal tag
     * (`0061_retention`) when the running code's journal has it, else `null`;
     * and the count of applied migrations either way. A reader that meets a
     * column it does not know can say *which* schema wrote it.
     */
    schema: z.object({
        migration: z.string().nullable(),
        applied: z.number().int().nonnegative(),
    }),
    /** Per table, in roster order. Every table appears, `0` when empty. */
    row_counts: z.record(z.string(), z.number().int().nonnegative()),
    /** Σ row_counts, for the eye. */
    total_rows: z.number().int().nonnegative(),
    /** SHA-256, lower-case hex, of the UTF-8 bytes of the `tables` value. */
    body_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    body_bytes: z.number().int().nonnegative(),
});
export const TenantExportHeader = z.object({
    format: z.literal(TENANT_EXPORT_FORMAT),
    export_version: z.literal(TENANT_EXPORT_VERSION),
    tenant_id: z.string().min(1),
});
/** One row as stored: column name → JSON value. */
export const TenantExportRow = z.record(z.string(), z.unknown());
/** The `tables` value: table name → rows, in roster order. */
export const TenantExportTables = z.record(z.string(), z.array(TenantExportRow));
/**
 * The bytes before the body. Written by the store, recognized here, so that
 * the two cannot drift: `{"format":"…","export_version":1,"tenant_id":"…","tables":`.
 */
export function tenantExportHead(tenantId) {
    const header = {
        format: TENANT_EXPORT_FORMAT,
        export_version: TENANT_EXPORT_VERSION,
        tenant_id: tenantId,
    };
    return `${JSON.stringify(header).slice(0, -1)},"tables":`;
}
/** The bytes between the body and the manifest. The manifest is always last. */
export const TENANT_EXPORT_MANIFEST_MARKER = ',"manifest":';
/** The bytes after the manifest. */
export const TENANT_EXPORT_TAIL = '}';
/**
 * Cut a document into header, body and manifest **without parsing the body**.
 *
 * The body is found by layout rather than by parsing, because the whole point
 * of the hash is to be checked against the bytes on disk: `JSON.parse` then
 * `JSON.stringify` would verify a re-serialization, and a re-serialization can
 * agree with a corrupted file (a number that lost precision, a key order the
 * engine chose). The head is a fixed function of the tenant id; the manifest
 * is the last key and its own text cannot contain the marker (its keys are
 * fixed and its values are a slug, an instant, integers, a hex string and a
 * map keyed by table names); so the last occurrence of `,"manifest":` is the
 * boundary even when a row somewhere in the body carries a JSONB key named
 * `manifest`.
 */
export function splitTenantExport(text) {
    const headerEnd = text.indexOf(',"tables":');
    if (!text.startsWith('{"format":') || headerEnd < 0) {
        throw new Error('not a tenant export: the document does not start with its header');
    }
    const header = TenantExportHeader.parse(JSON.parse(`${text.slice(0, headerEnd)}}`));
    const head = tenantExportHead(header.tenant_id);
    if (!text.startsWith(head)) {
        throw new Error('not a tenant export: the header is not in the written layout');
    }
    const manifestAt = text.lastIndexOf(TENANT_EXPORT_MANIFEST_MARKER);
    if (manifestAt < head.length || !text.endsWith(TENANT_EXPORT_TAIL)) {
        throw new Error('not a tenant export: no manifest at the end of the document');
    }
    const manifestText = text.slice(manifestAt + TENANT_EXPORT_MANIFEST_MARKER.length, text.length - TENANT_EXPORT_TAIL.length);
    const manifest = TenantExportManifest.parse(JSON.parse(manifestText));
    if (manifest.tenant_id !== header.tenant_id) {
        throw new Error(`not a tenant export: header names ${header.tenant_id}, manifest names ${manifest.tenant_id}`);
    }
    return { header, body: text.slice(head.length, manifestAt), manifest };
}
/**
 * Verify, then parse. `sha256Hex` is the caller's (`node:crypto` in the store
 * and the importer); this package stays free of it.
 *
 * Every mismatch is an error rather than a flag, because the only caller that
 * wants to proceed past a bad hash is one that is about to land the wrong rows
 * into a tenant.
 */
export function parseTenantExport(text, sha256Hex) {
    const { header, body, manifest } = splitTenantExport(text);
    const digest = sha256Hex(body);
    if (digest !== manifest.body_sha256) {
        throw new Error(`the export body does not match its manifest: sha256 ${digest}, manifest says ${manifest.body_sha256}`);
    }
    const tables = TenantExportTables.parse(JSON.parse(body));
    for (const [table, rows] of Object.entries(tables)) {
        const counted = manifest.row_counts[table];
        if (counted !== rows.length) {
            throw new Error(`the export body does not match its manifest: ${table} has ${String(rows.length)} rows, ` +
                `manifest says ${counted === undefined ? 'nothing' : String(counted)}`);
        }
    }
    for (const table of Object.keys(manifest.row_counts)) {
        if (!(table in tables)) {
            throw new Error(`the export body does not match its manifest: ${table} is counted but absent`);
        }
    }
    return { header, tables, manifest };
}
//# sourceMappingURL=export.js.map